// Marketing content for the industry solution pages (/solutions/:industry) and
// the product walkthrough page (/product). Copy lives here so it can be edited
// without touching JSX. Each industry is SEO-targeted at one sector.

export const INDUSTRY_LIST = ["healthcare", "saas", "financial-services", "retail"];

export const INDUSTRIES = {
  healthcare: {
    slug: "healthcare",
    name: "Healthcare",
    icon: "HeartPulse",
    accent: "#3B82F6",
    navBlurb: "Readmissions, occupancy & CMS reporting — answered, with an audit trail.",
    metaTitle: "Healthcare Analytics & AI Semantic Layer | Sema",
    metaDescription:
      "Sema gives healthcare and senior-living teams governed, plain-English answers on readmissions, occupancy, staffing (NHPPD) and payer mix — every answer auditable and HIPAA-aware.",
    keywords:
      "healthcare analytics, hospital readmissions analytics, senior living occupancy, NHPPD staffing, payer mix, CMS reporting, HIPAA AI, healthcare semantic layer",
    eyebrow: "Sema for Healthcare",
    h1: "Governed answers for clinical and operational data.",
    subtitle:
      "Connect your EHR, occupancy, staffing and finance data. Sema learns what every column means and answers questions on readmissions, census, NHPPD and payer mix in plain English — with PHI masked and every answer audit-logged.",
    problem: {
      title: "Clinical truth is buried across a dozen systems.",
      body: "Readmission rates, occupancy, case mix and payroll live in different schemas with cryptic column names. Analysts spend days reconciling definitions, and AI on top of undocumented PHI is a compliance risk waiting to happen.",
    },
    useCases: [
      { icon: "Activity", title: "30-day readmissions", body: "Track risk-adjusted readmission rates by facility, diagnosis and discharge cohort — without hand-written SQL each time." },
      { icon: "BedDouble", title: "Occupancy & census", body: "Daily census, occupancy %, and move-in/move-out trends roll up across every community automatically." },
      { icon: "Users", title: "Staffing & NHPPD", body: "Nursing hours per patient day vs. acuity, flagged the moment a facility drifts below target." },
      { icon: "Receipt", title: "Payer mix & PMI", body: "Medicare/Medicaid/private split and per-member income trends, defined once and consistent everywhere." },
    ],
    metrics: [
      { stat: "100%", label: "PHI columns auto-flagged & masked" },
      { stat: "12", label: "clinical + ops tables modeled in minutes" },
      { stat: "1", label: "definition of every metric, org-wide" },
    ],
    faqs: [
      { q: "Is Sema HIPAA-aware?", a: "Yes. Sema auto-classifies PHI (SSN, DOB, names, contact info) as Restricted and refuses or masks it by role. Every query is logged to an append-only audit trail you can export for compliance reviews." },
      { q: "Does it work with our EHR?", a: "Sema connects to the warehouse or read replica your EHR/EMR feeds — Postgres, Snowflake, BigQuery and more — and introspects the schema directly. No EHR integration project required." },
      { q: "How is a 'readmission' defined so everyone agrees?", a: "You define it once as a governed metric in the semantic layer. Every chat answer, report and alert then resolves it the same way, so the number never drifts between dashboards." },
    ],
  },
  saas: {
    slug: "saas",
    name: "SaaS",
    icon: "Cloud",
    accent: "#8B5CF6",
    navBlurb: "Churn, NRR, MAU & pipeline — one definition the whole company trusts.",
    metaTitle: "SaaS Metrics & Revenue Analytics Semantic Layer | Sema",
    metaDescription:
      "Sema gives SaaS teams one governed definition of churn, NRR, MAU, ARR and pipeline — ask in plain English, get the SQL and the answer, with every metric consistent across GTM, finance and product.",
    keywords:
      "SaaS analytics, churn analysis, net revenue retention, NRR, MAU, ARR, pipeline analytics, product analytics, SaaS metrics semantic layer, self-serve analytics",
    eyebrow: "Sema for SaaS",
    h1: "One source of truth for every SaaS metric.",
    subtitle:
      "Churn, NRR, MAU, ARR, pipeline — defined once in the semantic layer and answered in plain English. Give GTM, finance and product the same trustworthy numbers without a BI backlog.",
    problem: {
      title: "Every team defines churn differently.",
      body: "Finance, GTM and product each compute retention their own way, in their own tool. The board deck disagrees with the dashboard, and an LLM bolted onto your warehouse just invents a fourth number.",
    },
    useCases: [
      { icon: "TrendingDown", title: "Churn & retention", body: "Gross and net retention by cohort, plan and segment — computed from one governed definition." },
      { icon: "Repeat", title: "Net revenue retention", body: "NRR with expansion, contraction and churn broken out, consistent in every report and the board deck." },
      { icon: "Users", title: "Activation & MAU", body: "Active-user and activation funnels that product and growth can both trust." },
      { icon: "Filter", title: "Pipeline & conversion", body: "Stage-by-stage pipeline velocity and win rates, joined across CRM and product usage." },
    ],
    metrics: [
      { stat: "1", label: "definition of NRR, everywhere" },
      { stat: "0", label: "BI tickets to answer a metric question" },
      { stat: "100%", label: "answers ship with the SQL they ran" },
    ],
    faqs: [
      { q: "Can Sema join product usage to CRM revenue?", a: "Yes. Sema verifies the joins between your product, billing and CRM tables once; from then on every cross-object question (e.g. 'NRR for accounts that adopted feature X') resolves correctly." },
      { q: "How does this beat just asking ChatGPT over our DB?", a: "Raw text-to-SQL guesses against undocumented columns and has no governance. Sema only references metrics and columns you've approved, shows its SQL and graph path, and enforces access by role." },
      { q: "Who can use it — only analysts?", a: "Anyone. PMs, AEs and execs ask in plain English; analysts own the governed metric definitions behind the scenes." },
    ],
  },
  "financial-services": {
    slug: "financial-services",
    name: "Financial Services",
    icon: "Landmark",
    accent: "#F59E0B",
    navBlurb: "Risk, exposure & regulatory reporting — explainable and fully audited.",
    metaTitle: "Financial Services Analytics & Governed AI | Sema",
    metaDescription:
      "Sema gives banks, lenders and fintechs explainable, audit-grade answers on exposure, delinquency, AUM and regulatory metrics — with row-level access, column masking and an immutable audit log.",
    keywords:
      "financial services analytics, risk analytics, regulatory reporting, delinquency analysis, AUM, exposure, banking semantic layer, explainable AI finance, audit log",
    eyebrow: "Sema for Financial Services",
    h1: "Explainable, audited answers for regulated finance.",
    subtitle:
      "Exposure, delinquency, AUM and regulatory metrics — answered in plain English with the SQL shown, access enforced by role, and an immutable record of who asked what, and when.",
    problem: {
      title: "In finance, an unexplained number is a liability.",
      body: "Regulators and risk committees need to know exactly how a figure was derived and who could see it. Black-box AI and screenshot-driven reporting can't survive an audit.",
    },
    useCases: [
      { icon: "ShieldAlert", title: "Risk & exposure", body: "Concentration, exposure and limit-utilization metrics defined once and traceable to source." },
      { icon: "CalendarClock", title: "Delinquency & aging", body: "Delinquency buckets and roll rates by product and segment, with frozen evidence on every alert." },
      { icon: "Landmark", title: "AUM & flows", body: "Assets under management, net flows and balances reconciled across systems." },
      { icon: "FileCheck", title: "Regulatory reporting", body: "Reusable, governed definitions that make periodic regulatory packs repeatable and defensible." },
    ],
    metrics: [
      { stat: "100%", label: "queries logged, append-only" },
      { stat: "RBAC", label: "row + column access by role" },
      { stat: "1-click", label: "evidence pack export" },
    ],
    faqs: [
      { q: "Can we prove how a number was calculated?", a: "Every answer ships with the exact SQL, the semantic-graph nodes it traversed, and the rows it sampled. Approved figures become immutable registry entries with full provenance." },
      { q: "How is sensitive data controlled?", a: "Restricted columns (PII/financial identifiers) are refused or masked by role at plan time — before any SQL runs — and every access is recorded in the audit log." },
      { q: "Can it run in our VPC?", a: "Yes. Sema's control plane can run in your network; generated SQL executes against your data plane, and the semantic graph never leaves your environment on the self-hosted tier." },
    ],
  },
  retail: {
    slug: "retail",
    name: "Retail",
    icon: "ShoppingCart",
    accent: "#EC4899",
    navBlurb: "Sales, inventory, margin & basket — answered across every channel.",
    metaTitle: "Retail & E-commerce Analytics Semantic Layer | Sema",
    metaDescription:
      "Sema gives retail and e-commerce teams governed answers on sell-through, inventory, margin and basket across stores and channels — ask in plain English and trust the number.",
    keywords:
      "retail analytics, e-commerce analytics, inventory analytics, sell-through, gross margin, basket analysis, omnichannel reporting, retail semantic layer",
    eyebrow: "Sema for Retail",
    h1: "Answers across every store, SKU and channel.",
    subtitle:
      "Sell-through, inventory, margin and basket — unified across stores and e-commerce and answered in plain English. Merchandisers and ops get the number without waiting on the data team.",
    problem: {
      title: "Channels multiply; definitions fragment.",
      body: "Stores, marketplace and DTC each report sales differently. 'Margin' means three things, inventory is stale by the time it reaches a deck, and nobody trusts the cross-channel roll-up.",
    },
    useCases: [
      { icon: "ShoppingBag", title: "Sell-through & sales", body: "Units and revenue by SKU, store and channel, on one consistent definition." },
      { icon: "Boxes", title: "Inventory & stockouts", body: "On-hand, weeks-of-supply and stockout risk flagged before it costs a sale." },
      { icon: "Percent", title: "Gross margin", body: "Margin after discounts and returns, defined once so finance and merchandising agree." },
      { icon: "ShoppingCart", title: "Basket & attach", body: "Basket size, attach rates and category mix, joined across order and product data." },
    ],
    metrics: [
      { stat: "Omni", label: "channels unified in one model" },
      { stat: "1", label: "definition of margin & sell-through" },
      { stat: "min", label: "from question to charted answer" },
    ],
    faqs: [
      { q: "Can it combine store and online sales?", a: "Yes. Sema verifies the joins across your POS, e-commerce and product tables once, so omnichannel questions resolve correctly every time." },
      { q: "How fresh is the data?", a: "Sema queries your warehouse directly (or a read replica), so answers reflect whatever your pipeline has loaded — no stale, hand-built extracts." },
      { q: "Do merchandisers need SQL?", a: "No. They ask in plain English and get a charted answer; analysts curate the governed metrics and verified joins underneath." },
    ],
  },
};

// Shared trust strip shown on every solution page.
export const SOLUTION_LOGOS = ["Northwind", "Atlas Mfg.", "Helix Bio", "Polaris Bank", "Vertex Retail", "Lumen Health"];

// Product / "How it works" page.
export const PRODUCT = {
  metaTitle: "How Sema Works — The Semantic Layer for Enterprise AI",
  metaDescription:
    "See how Sema connects your warehouse, auto-builds a glossary and relationship graph, answers questions in plain English, and governs every answer with role-based access and an audit log.",
  keywords:
    "semantic layer, text to SQL, governed AI analytics, data glossary, relationship graph, audit log, how Sema works, enterprise AI data",
  eyebrow: "How it works",
  h1: "From raw schema to a trusted answer.",
  subtitle:
    "Sema turns a database your AI can't reason about into a governed semantic layer it can — then answers business questions in plain English, showing its work every time.",
  steps: [
    {
      n: "01",
      key: "connect",
      title: "Connect your warehouse",
      body: "Point Sema at Postgres, Snowflake, BigQuery or a read replica. It introspects every table, column, type and key in seconds — and flags sensitive fields automatically.",
      shot: "/screenshots/schema.png",
      caption: "Live schema introspection — 12 tables across clinical, operations, finance & HR.",
    },
    {
      n: "02",
      key: "glossary",
      title: "Sema writes the glossary",
      body: "An LLM drafts a plain-English definition for every table and column, tags sensitivity and lifecycle, and embeds it for semantic search. Your team confirms — it never guesses silently.",
      shot: "/screenshots/glossary.png",
      caption: "Auto-generated, human-confirmed definitions with sensitivity & lifecycle.",
    },
    {
      n: "03",
      key: "graph",
      title: "Verify the semantic graph",
      body: "Sema infers the joins between tables and scores its confidence. Verify a join once and every future answer that needs it is correct — no SQL, no tribal knowledge.",
      shot: "/screenshots/semantics.png",
      caption: "Inferred vs. verified joins and first-class metrics, navigable as a graph.",
    },
    {
      n: "04",
      key: "ask",
      title: "Ask in plain English",
      body: "Anyone asks a question; Sema grounds it in the graph, generates read-only SQL, runs it, and auto-charts the result. It shows the SQL and the tables it touched — or asks a clarifying question instead of guessing.",
      shot: "/screenshots/chat.png",
      caption: "Plain-English question → grounded SQL → auto-charted, explainable answer.",
    },
    {
      n: "05",
      key: "govern",
      title: "Govern every answer",
      body: "Role-based access, restricted-column masking and an append-only audit log are on by default. Every question, plan and answer is recorded — the evidence pack for any decision, in one click.",
      shot: "/screenshots/audit.png",
      caption: "Append-only audit log — who asked what, when, and what ran.",
    },
  ],
  differentiation: {
    title: "Why Sema is different.",
    subtitle: "Three common approaches, and where they break.",
    columns: [
      {
        tag: "Raw text-to-SQL",
        them: "Guesses against undocumented columns, grabs the closest-named field, and has no idea what a metric means or who's allowed to see it.",
        sema: "Only references metrics, columns and joins you've approved — and refuses to guess when it can't ground the answer.",
      },
      {
        tag: "Traditional BI",
        them: "Locks logic inside one tool, needs an analyst for every new question, and the same metric drifts across dashboards.",
        sema: "One governed definition resolves everywhere — chat, reports, alerts and the API — and business users self-serve in plain English.",
      },
      {
        tag: "RAG over schema",
        them: "Embeds column names and hopes for the best; no joins, no governance, no proof of how an answer was produced.",
        sema: "A typed semantic graph with verified joins, plus the exact SQL, graph path and audit record behind every answer.",
      },
    ],
  },
};
